import React, { ReactNode, useState } from 'react'

import { Box } from '../box'
import { CalendarSheet } from '../calendar-sheet'
import { Popover, PopoverBackdrop, PopoverContent } from '../popover'
import { Pressable } from '../pressable'
import { TimePicker } from '../time-picker'

export type CalendarPopoverPlacement = 'bottom right' | 'bottom left' | 'top right' | 'top left'
export type CalendarPopoverChangeSource = 'date' | 'time'

export interface CalendarPopoverProps {
    value?: Date | null
    onChange: (date: Date, source: CalendarPopoverChangeSource) => void
    isDateDisabled?: (date: Date) => boolean
    placement?: CalendarPopoverPlacement
    disabled?: boolean
    /** Adds the clock toggle that switches the sheet to an hours/minutes picker. */
    withTime?: boolean
    triggerClassName?: string
    /** The clickable trigger content (e.g. an icon + label). */
    children: ReactNode
}

type PickerView = 'date' | 'time'

const startOfMonth = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), 1)

/**
 * Custom-trigger date picker: renders {@link children} as the trigger and opens
 * an anchored {@link Popover} containing a dark {@link CalendarSheet}. With
 * `withTime` the sheet's clock switches to a {@link TimePicker}; every spinner
 * step reports `(date, 'time')` so the host can follow the time live, while a
 * day pick reports `(date, 'date')` and closes the popover. Mirrors how
 * {@link DateTimePicker} drives the same two sheets.
 */
export const CalendarPopover: React.FC<CalendarPopoverProps> = ({
    value,
    onChange,
    isDateDisabled,
    placement = 'bottom right',
    disabled = false,
    withTime = false,
    triggerClassName,
    children,
}) => {
    const current = value ?? null
    const [isOpen, setIsOpen] = useState(false)
    const [view, setView] = useState<PickerView>('date')
    const [viewMonth, setViewMonth] = useState<Date>(current ?? new Date())
    const [draft, setDraft] = useState<Date>(current ?? new Date())

    const open = () => {
        const base = current ?? new Date()
        setView('date')
        setViewMonth(base)
        setDraft(base)
        setIsOpen(true)
    }

    const selectDay = (day: Date) => {
        const next = new Date(day)
        if (withTime) {
            next.setHours(draft.getHours(), draft.getMinutes(), 0, 0)
        }
        setDraft(next)
        onChange(next, 'date')
        setIsOpen(false)
    }

    const commitTime = (hours: number, minutes: number) => {
        const next = new Date(draft)
        next.setHours(hours, minutes, 0, 0)
        setDraft(next)
        onChange(next, 'time')
    }

    return (
        <Popover
            isOpen={isOpen && !disabled}
            onClose={() => setIsOpen(false)}
            trapFocus={false}
            focusScope={false}
            placement={placement}
            trigger={triggerProps => (
                <Pressable
                    {...triggerProps}
                    disabled={disabled}
                    onPress={open}
                    accessibilityRole='button'
                    className={triggerClassName}
                >
                    {children}
                </Pressable>
            )}
        >
            <PopoverBackdrop />
            <PopoverContent className='min-w-[256px] items-center rounded border border-solid border-border-subtle bg-background-elevation p-0'>
                {view === 'date' ? (
                    <CalendarSheet
                        month={viewMonth}
                        mode='default'
                        showTime={withTime}
                        selectedDate={current}
                        today={new Date()}
                        isDateDisabled={isDateDisabled}
                        onPrevMonth={() =>
                            setViewMonth(m =>
                                startOfMonth(new Date(m.getFullYear(), m.getMonth() - 1)),
                            )
                        }
                        onNextMonth={() =>
                            setViewMonth(m =>
                                startOfMonth(new Date(m.getFullYear(), m.getMonth() + 1)),
                            )
                        }
                        onSelectDate={selectDay}
                        onTimePress={() => setView('time')}
                    />
                ) : (
                    <Box className='items-center'>
                        <TimePicker
                            hours={draft.getHours()}
                            minutes={draft.getMinutes()}
                            onHoursChange={hours => commitTime(hours, draft.getMinutes())}
                            onMinutesChange={minutes => commitTime(draft.getHours(), minutes)}
                            onCalendarPress={() => setView('date')}
                        />
                    </Box>
                )}
            </PopoverContent>
        </Popover>
    )
}

CalendarPopover.displayName = 'CalendarPopover'
